/**
 * dataTableUtils.ts
 * ─────────────────
 * Fonctions utilitaires PURES extraites de DataTableView.vue.
 * Sans effet de bord, sans import Vue → 100 % testables en Vitest.
 */

// ─── Référencement ───────────────────────────────────────────────────────────

/**
 * Résout le type de référence d'une colonne.
 *
 * - Si `refLinkedTo` est renseigné (non null), il est retourné directement.
 * - Sinon, la valeur est cherchée dans `referenceTypeForReferencingColumns`
 *   en utilisant `idColumn` comme clé.
 *
 * @param idColumn                            Identifiant de la colonne
 * @param refLinkedTo                         Type de référence explicite (ou null)
 * @param referenceTypeForReferencingColumns  Map colonne→référence issue du chargement
 * @returns Type de référence résolu, ou undefined si introuvable
 */
export function addRefLinkedTo(
  idColumn: string,
  refLinkedTo: string | null,
  referenceTypeForReferencingColumns: Record<string, string>,
): string | undefined {
  if (refLinkedTo !== null) return refLinkedTo;
  const value = referenceTypeForReferencingColumns[idColumn];
  return value || undefined;
}

// ─── Détection de navigateur ──────────────────────────────────────────────────

/** Résultat de la détection du navigateur. */
export type BrowserColumnStrategy = "firefox" | "chromium" | "default";

/**
 * Détecte la stratégie de tri des colonnes selon le navigateur.
 *
 * Firefox utilise `toBeShownDataColumns`, Chromium/Safari `toBeShown`,
 * les autres navigateurs utilisent également `toBeShown` avec un log d'avertissement.
 *
 * @param userAgent  `navigator.userAgent` de l'environnement courant
 */
export function detectBrowserColumnStrategy(userAgent: string): BrowserColumnStrategy {
  if (userAgent.indexOf("Firefox") >= 1) return "firefox";
  if (userAgent.indexOf("Safari") >= 1 && userAgent.indexOf("Chrome") >= 1) return "chromium";
  return "default";
}

// ─── Construction de la map des filtres ──────────────────────────────────────

import { markRaw } from "vue";
import { FilterListLeaf } from "@/model/data/FilterListTree";

/**
 * Une entrée de la map de filtres consommée par {@code FiltersDataCollapse}.
 * Deux formes possibles côté wire :
 *
 * <ul>
 *   <li><b>Legacy</b> : tableau de {@link FilterListLeaf} - valeurs d'une
 *       colonne {@code ReferenceChecker} agrégées au niveau du datatype.
 *       Indexé par {@code listName} ( = referenceType ).</li>
 *   <li><b>FILTER_LIST</b> : objet {@code { values, truncated }} -
 *       valeurs distinctes d'une colonne marquée {@code __FILTER_LIST__}.
 *       Indexé par {@code componentKey}.</li>
 * </ul>
 *
 * Le code consommateur disambigüise sur la forme ( {@code Array.isArray}
 * vs {@code !!entry.values} ) ; cf. {@code getListOptions} dans
 * {@code FiltersDataCollapse}.
 */
export type FilterListMapEntry
  = | FilterListLeaf[]
    | { values: Array<string | null>; truncated: boolean; hasEmpty: boolean };

/**
 * Transforme le tableau d'entrées retourné par {@code GET /filters} en
 * une map indexée :
 *
 * <ul>
 *   <li>par {@code listName} pour les {@link FilterList} ( filtres
 *       référence , historique ) - valeur = tableau de
 *       {@link FilterListLeaf} ;</li>
 *   <li>par {@code componentKey} pour les {@code ColumnDistinctValues}
 *       ( nouveau type pour les colonnes {@code __FILTER_LIST__} ) -
 *       valeur = {@code { values, truncated }}.</li>
 * </ul>
 *
 * Les deux types cohabitent dans la même réponse via la propriété
 * {@code @class} ( cf. {@code FilterListEntry} côté Java ). On
 * discrimine ici pour produire une map directement consommable côté
 * composants ( pas de re-discrimination à chaque accès ).
 */
export function buildFilterListMap(
  entries: any[],
): Record<string, FilterListMapEntry> {
  // PERF audit (8/5/26) - markRaw sur chaque valeur stockée dans la map
  // afin que useDataTable.filterLists ( reactive ) ne wrappe pas chaque
  // FilterListLeaf / chaque tableau de valeurs distinctes dans un Proxy
  // Vue. Sur un dataset ACBB ( ~10 dropdowns , milliers d'options chacune )
  // le proxying récursif coûte 200-1000 ms à chaque chargement de filtres
  // ; les données restent en lecture seule côté UI , la réactivité fine
  // ( par leaf ) n'apporte rien.
  return (entries || []).reduce<Record<string, FilterListMapEntry>>((acc, entry) => {
    if (!entry) return acc;
    const className: string | undefined = entry["@class"];
    // ColumnDistinctValues : valeurs distinctes d'une colonne FILTER_LIST.
    if (className === "fr.inra.oresing.persistence.ColumnDistinctValues"
      || (typeof entry.componentKey === "string" && Array.isArray(entry.values))) {
      acc[entry.componentKey] = markRaw({
        values: entry.values as Array<string | null>,
        truncated: !!entry.truncated,
        hasEmpty: !!entry.hasEmpty,
      });
      return acc;
    }
    // FilterList ( legacy ) : valeurs d'un ReferenceChecker.
    if (typeof entry.listName === "string") {
      acc[entry.listName] = markRaw(
        (entry.refsLinkeds || []).map((leaf: any) => markRaw(new FilterListLeaf(leaf))),
      );
      return acc;
    }
    return acc;
  }, {});
}
